import React from "react";
import { FlaskConical, Loader2, CheckCircle2, AlertTriangle, Mail, ArrowRight } from "lucide-react";
import { chipCls, fmtDateTime } from "./api";

function pickState(p, result, running) {
  if (p.deleted) return { label: "Contact removed", cls: "border-[#E8E1D2] bg-white text-[#A09680]", Icon: AlertTriangle };
  if (result?.ok) return { label: "In your inbox", cls: "border-[#3F6D55]/40 bg-[#EAF3EC] text-[#2E4A38]", Icon: Mail };
  if (result?.error) return { label: `Send failed · ${result.error}`, cls: "border-[#C84C21] bg-[#FCE9E1] text-[#C84C21]", Icon: AlertTriangle };
  if (p.drafted) return { label: "Drafted", cls: "border-[#C84C21]/40 bg-[#FCE9E1] text-[#C84C21]", Icon: CheckCircle2 };
  if (p.draft_error) return { label: `Draft failed · ${p.draft_error}`, cls: "border-[#C84C21] bg-white text-[#C84C21]", Icon: AlertTriangle };
  if (running) return { label: "Researching…", cls: "border-[#E8E1D2] bg-[#FAF7F1] text-[#5C6470]", Icon: Loader2 };
  return { label: "Not drafted", cls: "border-[#E8E1D2] bg-white text-[#A09680]", Icon: AlertTriangle };
}

function PickRow({ p, i, result, running, onFocus }) {
  const s = pickState(p, result, running);
  return (
    <li className="px-4 py-2.5 border-t border-[#F0EBDC] flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid={`test-pick-${p.id}`}>
      <span className="font-mono text-[10px] text-[#A09680] w-7">#{i + 1}</span>
      <div className="flex-1 min-w-[260px]">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-[13.5px] font-medium text-[#0F1825]">{p.company}</span>
          <span className="text-[12.5px] text-[#0F1825]">{p.full_name || p.email}</span>
          {p.designation && <span className="text-[12px] text-[#5C6470]">{p.designation}</span>}
          {p.role_label && <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[#4E378A] border border-[#E4DDF5] bg-[#F6F3FC] px-1.5 py-0.5" data-testid={`test-pick-role-${p.id}`}>{p.role_label}</span>}
        </div>
        {p.subject && <div className="font-mono text-[10.5px] text-[#5C6470] mt-0.5" data-testid={`test-pick-subject-${p.id}`}>{result?.subject || p.subject}</div>}
      </div>
      <span className={`${chipCls} ${s.cls}`} data-testid={`test-pick-state-${p.id}`}>
        <s.Icon size={10} className={s.Icon === Loader2 ? "animate-spin" : ""} /> {s.label}
      </span>
      {p.drafted && !p.deleted && (
        <button type="button" onClick={() => onFocus(p.email)} className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#C84C21] hover:text-[#0F1825] inline-flex items-center gap-1" data-testid={`test-pick-open-${p.id}`}>
          Open draft <ArrowRight size={11} />
        </button>
      )}
    </li>
  );
}

export function TestSetPanel({ job, onFocus }) {
  if (!job || job.scope !== "sample") return null;
  const running = job.status === "running";
  const picks = job.picks || [];
  const results = Object.fromEntries((job.test_results || []).map((r) => [r.contact_id, r]));
  const companies = new Set(picks.map((p) => p.company_key)).size;
  const roles = new Set(picks.map((p) => p.role_label)).size;
  const drafted = picks.filter((p) => p.drafted).length;
  const inbox = (job.test_results || []).filter((r) => r.ok).length;
  const to = (job.test_results || [])[0]?.to;
  return (
    <section className="bg-white border border-[#E8E1D2]" data-testid="test-set-panel">
      <header className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <FlaskConical size={15} className="text-[#C84C21] shrink-0" />
        <h3 className="font-serif text-[1.1rem] text-[#0F1825]">Test set</h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470]" data-testid="test-set-summary">
          {picks.length} scenario{picks.length === 1 ? "" : "s"} · {companies} compan{companies === 1 ? "y" : "ies"} · {roles} function{roles === 1 ? "" : "s"} · {drafted} drafted{job.send_tests ? ` · ${inbox} in your inbox` : ""}
        </span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680]" data-testid="test-set-status">
          {running ? (job.phase === "sending_tests" ? "emailing you…" : "drafting…") : `${job.status}${job.finished_at ? ` · ${fmtDateTime(job.finished_at)}` : ""}`}
        </span>
      </header>
      <p className="px-4 pb-3 text-[12.5px] text-[#5C6470] leading-relaxed">
        Five different companies, five different functions — one email each, drafted with the same research, role angle and signature the full campaign will use.
        {job.send_tests && to ? <> Check <strong className="text-[#0F1825]">{to}</strong> for subjects starting <span className="font-mono">[TEST 1/{picks.length}]</span> … <span className="font-mono">[TEST {picks.length}/{picks.length}]</span>.</> : null}
        {" "}Happy with them? Run <strong className="text-[#0F1825]">Research + draft all</strong>. Not yet? Adjust the campaign context, asset hooks or signature and generate a new set.
      </p>
      {job.test_error && (
        <div className="mx-4 mb-3 border-l-2 border-[#C84C21] bg-[#FDECE6] px-3 py-2 text-[12px] text-[#C84C21]" data-testid="test-set-error">Test emails could not be sent: {job.test_error}</div>
      )}
      <ol>
        {picks.map((p, i) => <PickRow key={p.id} p={p} i={i} result={results[p.id]} running={running} onFocus={onFocus} />)}
      </ol>
    </section>
  );
}
